(function () {
  'use strict';

  const VIEWS = {
    denmark: { west: 7.75, south: 54.45, east: 15.35, north: 57.92, label: 'Danmark', maxZoom: 7 },
    kobenhavn: { west: 12.35, south: 55.55, east: 12.80, north: 55.82, label: 'København', maxZoom: 11 },
    scandinavia: { west: 4.0, south: 54.4, east: 24.3, north: 71.5, label: 'Skandinavien', maxZoom: 4.2 },
    world: { west: -168, south: -58, east: 178, north: 76, label: 'Hele verden', maxZoom: 2.5 }
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  })[character]);

  const numeric = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const validBox = (box) =>
    box &&
    [box.north, box.south, box.east, box.west].every(
      (value) => numeric(value) !== null
    ) &&
    numeric(box.south) < numeric(box.north) &&
    numeric(box.west) < numeric(box.east);

  const center = (box) => [
    (box.west + box.east) / 2,
    (box.south + box.north) / 2
  ];

  const area = (box) =>
    Math.max(
      1e-9,
      (box.east - box.west) * (box.north - box.south)
    );

  const intersects = (left, right) =>
    !(
      left.west > right.east ||
      left.east < right.west ||
      left.south > right.north ||
      left.north < right.south
    );

  const intersectionArea = (left, right) => {
    const west = Math.max(left.west, right.west);
    const east = Math.min(left.east, right.east);
    const south = Math.max(left.south, right.south);
    const north = Math.min(left.north, right.north);

    return east > west && north > south
      ? (east - west) * (north - south)
      : 0;
  };

  const distanceScore = (left, right) => {
    const [leftX, leftY] = center(left);
    const [rightX, rightY] = center(right);

    const dx =
      (leftX - rightX) *
      Math.cos(((leftY + rightY) / 2) * Math.PI / 180);

    const dy = leftY - rightY;

    return 1 / (
      1 +
      Math.sqrt(dx * dx + dy * dy) * 7.5
    );
  };

  const formatPrice = (value) => {
    const parsed = Number(
      String(value || '').replace(',', '.')
    );

    return Number.isFinite(parsed)
      ? `${Math.round(parsed).toLocaleString('da-DK')} kr.`
      : '';
  };

  function init(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const dataset = window.GAMLESTREGER_GEO_DATA;
    const maplibregl = window.maplibregl;
    const find = (role) =>
      root.querySelector(`[data-role="${role}"]`);

    const grid = find('product-grid');
    const heading = find('result-heading');
    const resultMeta = find('result-meta');
    const emptyState = find('empty-state');
    const searchInput = find('place-search');
    const searchButton = find('place-search-button');
    const searchResults = find('search-results');
    const resultsPanel = find('results-panel');
    const resultsToggle = find('results-toggle');
    const resultsOpen = find('results-open');
    const resultsOpenLabel = find('results-open-label');

    const defaultViewKey = VIEWS[root.dataset.defaultView]
      ? root.dataset.defaultView
      : 'denmark';

    const PAGE_SIZE = 20;
    const showPrices =
      root.dataset.showPrices === 'true';

    if (
      !dataset ||
      !Array.isArray(dataset.products) ||
      !dataset.products.length
    ) {
      heading.textContent =
        'Kortene kunne ikke indlæses';
      resultMeta.textContent =
        'Geo-datasættet mangler i temaets assets.';
      return;
    }

    if (!maplibregl) {
      heading.textContent =
        'Kortet kunne ikke indlæses';
      resultMeta.textContent =
        'Kortbiblioteket er midlertidigt utilgængeligt.';
      return;
    }

    if (
      root.dataset.mapWorkerUrl &&
      typeof maplibregl.setWorkerUrl === 'function'
    ) {
      maplibregl.setWorkerUrl(
        root.dataset.mapWorkerUrl
      );
    }

    const products = dataset.products;

    const loadMoreSentinel =
      document.createElement('div');

    loadMoreSentinel.className =
      'gs-geo__load-more';

    loadMoreSentinel.setAttribute(
      'aria-hidden',
      'true'
    );

    grid.insertAdjacentElement(
      'afterend',
      loadMoreSentinel
    );

    let moveTimer = null;
    let currentRanked = [];
    let visibleCount = 0;
    let isAppending = false;
    let resultsCollapsed = false;

    find('dataset-count').textContent =
      `${products.length.toLocaleString('da-DK')} historiske kort`;

    const isTouchDevice = window.matchMedia(
      '(hover: none) and (pointer: coarse)'
    ).matches;

    const map = new maplibregl.Map({
      container: find('map'),
      style: 'https://tiles.openfreemap.org/styles/liberty',
      center: [10.15, 56.15],
      zoom: 5.7,
      minZoom: 2.2,
      maxZoom: 15,
      attributionControl: true,
      cooperativeGestures: isTouchDevice,
      locale: {
        'CooperativeGesturesHandler.MobileHelpText':
          'Brug to fingre til at flytte kortet'
      }
    });

    map.addControl(
      new maplibregl.NavigationControl({
        showCompass: false
      }),
      'bottom-left'
    );

    const resizeMap = () => map.resize();

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(resizeMap)
        : null;

    if (resizeObserver) {
      resizeObserver.observe(find('map'));
    }

    requestAnimationFrame(resizeMap);

    const hasDesktopDrawer = () =>
      window.matchMedia('(min-width: 901px)').matches &&
      !resultsCollapsed;

    const drawerWidth = () =>
      hasDesktopDrawer() && resultsPanel
        ? resultsPanel.getBoundingClientRect().width + 36
        : 0;

    const fitPadding = (base) => ({
      top: base,
      bottom: base,
      left: base,
      right: base + drawerWidth()
    });

    function primaryBox(geo) {
      const box = {
        north: numeric(geo && geo.bbox_north),
        south: numeric(geo && geo.bbox_south),
        east: numeric(geo && geo.bbox_east),
        west: numeric(geo && geo.bbox_west),
        name:
          (geo && geo.place_name) ||
          'Primært område'
      };

      return validBox(box) ? box : null;
    }

    function partBoxes(geo) {
      return (
        Array.isArray(geo && geo.coverage_parts)
          ? geo.coverage_parts
          : []
      )
        .map((part) => ({
          name:
            part.name || 'Dækningsområde',
          north: numeric(part.north),
          south: numeric(part.south),
          east: numeric(part.east),
          west: numeric(part.west)
        }))
        .filter(validBox);
    }

    function matchingBoxes(product) {
      const geo = product.geo || {};
      const parts = partBoxes(geo);

      if (
        geo.match_mode === 'parts' &&
        parts.length
      ) {
        return parts;
      }

      const primary = primaryBox(geo);
      return primary ? [primary] : parts;
    }

    function rankScore(
      product,
      productBox,
      viewBox
    ) {
      const productArea = area(productBox);
      const viewArea = area(viewBox);
      const intersection =
        intersectionArea(productBox, viewBox);

      const viewCoverage =
        intersection / viewArea;

      const productCoverage =
        intersection / productArea;

      const ratio =
        productArea / viewArea;

      const scaleMatch = Math.exp(
        -Math.abs(
          Math.log(Math.max(ratio, 1e-9))
        ) * 0.72
      );

      const proximity =
        distanceScore(productBox, viewBox);

      const [viewX, viewY] =
        center(viewBox);

      const containsCenter =
        viewX >= productBox.west &&
        viewX <= productBox.east &&
        viewY >= productBox.south &&
        viewY <= productBox.north;

      const type = String(
        (product.geo || {}).type || ''
      ).toLowerCase();

      const typeBoosts = {
        site: 0.10,
        district: 0.08,
        city: 0.07,
        municipality: 0.05,
        region: 0.035,
        country: 0.02,
        subcontinent: 0.01,
        continent: 0,
        world: 0
      };

      const typeBonus =
        (typeBoosts[type] || 0) *
        scaleMatch;

      const broadness = Math.max(
        0,
        Math.min(
          1,
          Math.log10(
            Math.max(viewArea, 1e-9) /
            0.25
          )
        )
      );

      const broadViewPenalties = {
        site: 0.20,
        district: 0.17,
        city: 0.15,
        municipality: 0.06
      };

      const broadViewPenalty =
        (broadViewPenalties[type] || 0) *
        broadness;

      return (
        scaleMatch * 0.55 +
        viewCoverage * 0.18 +
        productCoverage * 0.12 +
        proximity * 0.10 +
        (containsCenter ? 0.05 : 0) +
        typeBonus -
        broadViewPenalty
      );
    }

    function currentViewBox() {
      const mapContainer = map.getContainer();
      const width = mapContainer.clientWidth;
      const height = mapContainer.clientHeight;
      const visibleWidth = Math.max(
        1,
        width - drawerWidth()
      );

      const northWest = map.unproject([0, 0]);
      const southEast = map.unproject([
        visibleWidth,
        height
      ]);

      return {
        west: northWest.lng,
        south: southEast.lat,
        east: southEast.lng,
        north: northWest.lat
      };
    }

    function rankedProducts(view) {
      const ranked = [];

      products.forEach((product) => {
        let best = null;

        matchingBoxes(product).forEach(
          (box) => {
            if (!intersects(box, view)) {
              return;
            }

            const score = rankScore(
              product,
              box,
              view
            );

            if (
              !best ||
              score > best.score
            ) {
              best = {
                box,
                score
              };
            }
          }
        );

        if (best) {
          ranked.push({
            product,
            box: best.box,
            score: best.score
          });
        }
      });

      ranked.sort(
        (left, right) =>
          right.score - left.score
      );

      return ranked;
    }

    function createProductCard(product) {
      const price = showPrices
        ? formatPrice(product.price)
        : '';

      const card =
        document.createElement('article');

      card.className = 'gs-geo__card';

      card.innerHTML = `
        <div class="gs-geo__product-image">
          <div class="gs-geo__placeholder">
            Historisk kort
          </div>
        </div>

        <div class="gs-geo__card-body">
          <h4>${escapeHtml(product.title)}</h4>

          ${price
            ? `
              <div class="gs-geo__card-footer">
                <span class="gs-geo__price">
                  fra ${escapeHtml(price)}
                </span>
              </div>
            `
            : ''
          }
        </div>

        <a
          class="gs-geo__product-link"
          href="${escapeHtml(product.product_url)}"
          aria-label="Se ${escapeHtml(product.title)}"
        ></a>
      `;

      if (product.image_url) {
        const image =
          document.createElement('img');

        image.src = product.image_url;
        image.alt = product.title;
        image.loading = 'lazy';

        image.addEventListener(
          'error',
          () => image.remove()
        );

        card
          .querySelector(
            '.gs-geo__product-image'
          )
          .appendChild(image);
      }

      return card;
    }

    function updateResultMeta() {
      const total = currentRanked.length;

      const shown = Math.min(
        visibleCount,
        total
      );

      resultMeta.textContent = total
        ? `Kort, der bedst matcher udsnittets størrelse, vises først · ${shown.toLocaleString('da-DK')} af ${total.toLocaleString('da-DK')} vises.`
        : '';
    }

    function appendNextPage() {
      if (
        isAppending ||
        visibleCount >= currentRanked.length
      ) {
        return;
      }

      isAppending = true;

      const end = Math.min(
        visibleCount + PAGE_SIZE,
        currentRanked.length
      );

      const fragment =
        document.createDocumentFragment();

      currentRanked
        .slice(visibleCount, end)
        .forEach(({ product }) => {
          fragment.appendChild(
            createProductCard(product)
          );
        });

      grid.appendChild(fragment);
      visibleCount = end;

      updateResultMeta();

      loadMoreSentinel.classList.toggle(
        'is-hidden',
        visibleCount >= currentRanked.length
      );

      isAppending = false;
    }

    function render() {
      currentRanked = rankedProducts(
        currentViewBox()
      );

      visibleCount = 0;
      isAppending = false;

      grid.replaceChildren();

      if (resultsPanel) {
        resultsPanel.scrollTop = 0;
      }

      heading.textContent = currentRanked.length
        ? `${currentRanked.length.toLocaleString('da-DK')} kort matcher`
        : 'Ingen kort matcher';

      if (resultsOpenLabel) {
        resultsOpenLabel.textContent = currentRanked.length
          ? `${currentRanked.length.toLocaleString('da-DK')} kort`
          : 'Vis kort';
      }

      emptyState.classList.toggle(
        'is-hidden',
        currentRanked.length > 0
      );

      loadMoreSentinel.classList.toggle(
        'is-hidden',
        currentRanked.length === 0
      );

      updateResultMeta();
      appendNextPage();
    }

    const loadMoreObserver =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(
            (entries) => {
              if (
                entries.some(
                  (entry) =>
                    entry.isIntersecting
                )
              ) {
                appendNextPage();
              }
            },
            {
              root: null,
              rootMargin: '400px 0px',
              threshold: 0
            }
          )
        : null;

    if (loadMoreObserver) {
      loadMoreObserver.observe(
        loadMoreSentinel
      );
    }

    function moveTo(viewKey) {
      const view =
        VIEWS[viewKey] || VIEWS.denmark;

      map.fitBounds(
        [
          [view.west, view.south],
          [view.east, view.north]
        ],
        {
          padding: fitPadding(55),
          duration: 800,
          maxZoom: view.maxZoom || 11
        }
      );
    }

    function setResultsCollapsed(
      collapsed,
      persist
    ) {
      resultsCollapsed = Boolean(collapsed) &&
        window.matchMedia('(min-width: 901px)').matches;

      root.classList.toggle(
        'is-results-collapsed',
        resultsCollapsed
      );

      if (resultsToggle) {
        resultsToggle.setAttribute(
          'aria-expanded',
          String(!resultsCollapsed)
        );

        resultsToggle.setAttribute(
          'aria-label',
          resultsCollapsed
            ? 'Vis resultater'
            : 'Skjul resultater'
        );
      }

      if (resultsOpen) {
        resultsOpen.setAttribute(
          'aria-expanded',
          String(!resultsCollapsed)
        );
      }

      if (persist) {
        try {
          localStorage.setItem(
            'gs-geo-results-collapsed',
            resultsCollapsed ? '1' : '0'
          );
        } catch (_) {
          // no-op
        }
      }

      window.setTimeout(() => {
        map.resize();

        if (map.loaded()) {
          render();
        }
      }, 300);
    }

    async function searchPlace() {
      const query =
        searchInput.value.trim();

      if (query.length < 2) return;

      searchButton.disabled = true;
      searchButton.classList.add('is-loading');
      searchButton.setAttribute('aria-label', 'Søger efter sted');
      searchResults.replaceChildren();

      try {
        const params =
          new URLSearchParams({
            q: query,
            format: 'jsonv2',
            limit: '6',
            addressdetails: '1',
            'accept-language': 'da,en'
          });

        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?${params.toString()}`,
          {
            headers: {
              Accept: 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            'Stedsøgningen svarer ikke lige nu'
          );
        }

        const results =
          await response.json();

        results.forEach((item) => {
          const bounds =
            item.boundingbox || [];

          if (bounds.length !== 4) return;

          const option =
            document.createElement('div');

          option.className =
            'gs-geo__search-result';

          const name =
            item.name ||
            String(
              item.display_name || ''
            ).split(',')[0];

          option.innerHTML = `
            <strong>${escapeHtml(name)}</strong>
            ${escapeHtml(item.display_name || '')}
          `;

          option.addEventListener(
            'click',
            () => {
              map.fitBounds(
                [
                  [
                    Number(bounds[2]),
                    Number(bounds[0])
                  ],
                  [
                    Number(bounds[3]),
                    Number(bounds[1])
                  ]
                ],
                {
                  padding: fitPadding(80),
                  duration: 850,
                  maxZoom: 11
                }
              );

              searchResults.classList.add(
                'is-hidden'
              );
            }
          );

          searchResults.appendChild(option);
        });

        if (!searchResults.children.length) {
          const empty =
            document.createElement('div');

          empty.className =
            'gs-geo__search-result';

          empty.textContent =
            'Ingen steder fundet.';

          searchResults.appendChild(empty);
        }

        searchResults.classList.remove(
          'is-hidden'
        );
      } catch (error) {
        const message =
          document.createElement('div');

        message.className =
          'gs-geo__search-result';

        message.textContent =
          error.message ||
          'Søgningen fejlede';

        searchResults.appendChild(message);

        searchResults.classList.remove(
          'is-hidden'
        );
      } finally {
        searchButton.disabled = false;
        searchButton.classList.remove('is-loading');
        searchButton.setAttribute('aria-label', 'Søg efter sted');
      }
    }

    if (resultsToggle) {
      resultsToggle.addEventListener(
        'click',
        () => setResultsCollapsed(true, true)
      );
    }

    if (resultsOpen) {
      resultsOpen.addEventListener(
        'click',
        () => setResultsCollapsed(false, true)
      );
    }

    searchButton.addEventListener(
      'click',
      searchPlace
    );

    searchInput.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          searchPlace();
        }
      }
    );

    const resetMap = find('reset-map');

    if (resetMap) {
      resetMap.addEventListener(
        'click',
        () => moveTo('denmark')
      );
    }

    find('empty-reset').addEventListener(
      'click',
      () => moveTo('denmark')
    );

    root
      .querySelectorAll('[data-place]')
      .forEach((button) =>
        button.addEventListener(
          'click',
          () =>
            moveTo(
              button.dataset.place
            )
        )
      );

    map.on('moveend', () => {
      clearTimeout(moveTimer);

      moveTimer = setTimeout(
        render,
        90
      );
    });

    map.on('load', () => {
      const view = VIEWS[defaultViewKey];

      map.fitBounds(
        [
          [view.west, view.south],
          [view.east, view.north]
        ],
        {
          padding: fitPadding(45),
          duration: 0,
          maxZoom: view.maxZoom || 11
        }
      );

      render();
    });

    let savedResultsCollapsed = false;

    try {
      savedResultsCollapsed =
        localStorage.getItem(
          'gs-geo-results-collapsed'
        ) === '1';
    } catch (_) {
      // no-op
    }

    setResultsCollapsed(
      savedResultsCollapsed,
      false
    );

    const handleViewportChange = () => {
      if (
        !window.matchMedia('(min-width: 901px)').matches &&
        resultsCollapsed
      ) {
        setResultsCollapsed(false, false);
      } else {
        map.resize();
      }
    };

    window.addEventListener(
      'resize',
      handleViewportChange,
      { passive: true }
    );

    root.addEventListener(
      'shopify:block:select',
      () => map.resize()
    );

    root._gsGeoDestroy = () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (loadMoreObserver) {
        loadMoreObserver.disconnect();
      }

      window.removeEventListener(
        'resize',
        handleViewportChange
      );

      map.remove();
    };
  }

  function initializeAll(scope) {
    (scope || document)
      .querySelectorAll(
        '[data-gs-geo-explorer]'
      )
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll(document)
    );
  } else {
    initializeAll(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) =>
      initializeAll(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        event.target.matches &&
        event.target.matches(
          '[data-gs-geo-explorer]'
        )
          ? event.target
          : event.target.querySelector &&
            event.target.querySelector(
              '[data-gs-geo-explorer]'
            );

      if (root && root._gsGeoDestroy) {
        root._gsGeoDestroy();
      }
    }
  );
})();
